import React from "react";
import { CFormGroup, CLabel, CSelect, CSwitch } from "@coreui/react";
import { AccountSummaryData } from "../../../AccountSummary.slice";
import { ServiceAccountLabel } from "../../../components/ServiceAccountLabel";
import AccountStatusBadge from "./AutopayAccountStatusBadge";
import { DelegateAutopayInfoAlert } from "../../../components/DelegateAutopayInfoAlert";
import {
  canEnableAutopayForAccount,
  isDelegateAutopayAccount,
  isOwnerEnrolledInAutopay,
} from "./setAutopayPaymentMethod";
import {
  AutopayPaymentMethodOption,
  buildAutopayChangeSourcePaymentMethodOptions,
  buildAutopayEnrollmentPaymentMethodOptions,
} from "./autopayPaymentMethodOptions";
import { isEligibleAutopayPaymentMethod } from "../../PaymentMethods/utils";

interface PropsInterface {
  accounts: AccountSummaryData[];
  currentAccountId: string;
  paymentMethods: AutopayPaymentMethodOption[];
  pendingPaymentMethodIdsByAccount: Record<string, string>;
  onPendingPaymentMethodChange: (
    accountId: string,
    paymentMethodId: string
  ) => void;
  disabled?: boolean;
  onEnableAccounts: (accounts: AccountSummaryData[]) => void;
  onDisableAccounts: (accounts: AccountSummaryData[]) => void;
  onPaymentMethodChangeRequest: (
    account: AccountSummaryData,
    paymentMethod: AutopayPaymentMethodOption
  ) => void;
}

export const AutopayAccountManagementList: React.FC<PropsInterface> = ({
  accounts,
  currentAccountId,
  paymentMethods,
  pendingPaymentMethodIdsByAccount,
  onPendingPaymentMethodChange,
  disabled = false,
  onEnableAccounts,
  onDisableAccounts,
  onPaymentMethodChangeRequest,
}) => {
  const enrolledCount = accounts.filter((account) =>
    isOwnerEnrolledInAutopay(account)
  ).length;
  const enableEligibleAccounts = accounts.filter(canEnableAutopayForAccount);
  const disableEligibleAccounts = accounts.filter(isOwnerEnrolledInAutopay);

  const enrollmentPaymentMethodOptions =
    buildAutopayEnrollmentPaymentMethodOptions(paymentMethods);

  return (
    <CFormGroup className="autopay-account-management">
      <div className="autopay-account-management-header">
        <div>
          <CLabel className="mb-1">AutoPay by service account</CLabel>
          <p className="text-muted mb-0">
            {enrolledCount} of {accounts.length} service accounts enrolled in
            AutoPay.
          </p>
        </div>
        <div className="autopay-account-management-bulk-actions">
          {enableEligibleAccounts.length > 0 && (
            <button
              type="button"
              className="btn btn-link btn-sm autopay-account-selection-action"
              disabled={disabled}
              onClick={() => onEnableAccounts(enableEligibleAccounts)}
            >
              Enable all
            </button>
          )}
          {disableEligibleAccounts.length > 0 && (
            <button
              type="button"
              className="btn btn-link btn-sm autopay-account-selection-action"
              disabled={disabled}
              onClick={() => onDisableAccounts(disableEligibleAccounts)}
            >
              Disable all
            </button>
          )}
        </div>
      </div>

      <div className="autopay-account-management-list">
        <div className="autopay-account-management-row autopay-account-management-row-header">
          <span>Service account</span>
          <span>Payment method</span>
          <span>AutoPay</span>
        </div>
        {accounts.map((account) => {
          const isCurrentAccount = account.id === currentAccountId;
          const isDelegate = isDelegateAutopayAccount(account);
          const isEnrolled = isOwnerEnrolledInAutopay(account);
          const canEnable = canEnableAutopayForAccount(account);
          const isSwitchChecked = Boolean(account.autopay?.enabled);
          const isSwitchDisabled =
            disabled || isDelegate || (!isEnrolled && !canEnable);

          return (
            <div
              key={account.id}
              className={`autopay-account-management-row${
                isDelegate ? " autopay-account-management-row--delegate" : ""
              }`}
            >
              <div className="autopay-account-management-account">
                <ServiceAccountLabel
                  account={account}
                  nameBadge={
                    isCurrentAccount ? (
                      <AccountStatusBadge variant="current" />
                    ) : isDelegate ? (
                      <AccountStatusBadge variant="delegate" />
                    ) : account.sharedAccount &&
                      account.autopay?.owner === false ? (
                      <AccountStatusBadge variant="shared" />
                    ) : undefined
                  }
                />
              </div>
              <div className="autopay-account-management-payment-source">
                {isEnrolled && !isDelegate ? (
                  <CSelect
                    id={`autopay-payment-source-${account.id}`}
                    value={account.autopay?.paymentMethodId || ""}
                    disabled={disabled}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const newId = e.target.value;
                      if (
                        !newId ||
                        newId === account.autopay?.paymentMethodId
                      ) {
                        return;
                      }

                      const selectedPaymentMethod = paymentMethods.find(
                        (paymentMethod) => paymentMethod.id === newId
                      );
                      if (
                        selectedPaymentMethod &&
                        isEligibleAutopayPaymentMethod(selectedPaymentMethod)
                      ) {
                        onPaymentMethodChangeRequest(
                          account,
                          selectedPaymentMethod
                        );
                      }
                    }}
                  >
                    {buildAutopayChangeSourcePaymentMethodOptions(
                      paymentMethods,
                      account.autopay?.paymentMethodId
                    )}
                  </CSelect>
                ) : isDelegate ? (
                  <DelegateAutopayInfoAlert variant="inline" className="mb-0" />
                ) : canEnable ? (
                  <CSelect
                    id={`autopay-pending-payment-source-${account.id}`}
                    value={pendingPaymentMethodIdsByAccount[account.id] || ""}
                    disabled={disabled}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      onPendingPaymentMethodChange(account.id, e.target.value)
                    }
                  >
                    {enrollmentPaymentMethodOptions}
                  </CSelect>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </div>
              <div className="autopay-account-management-toggle">
                <CSwitch
                  className="preference-switch"
                  color="success"
                  labelOn="On"
                  labelOff="Off"
                  value="success"
                  shape="pill"
                  checked={isSwitchChecked}
                  disabled={isSwitchDisabled}
                  onChange={() => {
                    if (isEnrolled) {
                      onDisableAccounts([account]);
                      return;
                    }

                    onEnableAccounts([account]);
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </CFormGroup>
  );
};
